'use client';

import { c as _c } from "react/compiler-runtime";
import type { DrawForecast, DrawResult } from '@svenskaspelab/poolbetting/schema/forecast';
import type { DateTime } from '@svenskaspelab/poolbetting/schema/frameworks';
import type { EventStatus, Participant, SportType } from '@svenskaspelab/poolbetting/schema/sport';
import { ParticipantName } from '@svenskaspelab/poolbetting/ui/Coupon';
import { useGetMatchStatus } from '@svenskaspelab/poolbetting/ui/Init/client';
import type { ReactNode } from 'react';
import { SportinfoModalCouponResult } from './SportinfoModalCouponResult';
import { SportinfoModalMatchStatus } from './SportinfoModalMatchStatus';
type DrawEventResult = DrawForecast['drawResults'][number] | DrawResult['drawResults'][number];
type SportinfoModalHeaderResultPresentationalProps = {
  participants: (Participant | undefined)[] | undefined;
  eventDescription: string;
  eventStatus: EventStatus;
  sportType: SportType;
  matchStart: DateTime;
  statusTime: DateTime;
  matchResultState?: DrawEventResult['matchResult']['client'];
  result?: DrawEventResult['result'];
  isCancelled?: DrawEventResult['isCancelled'];
};

/**
 * Header for the sportinfo modal when the draw is live or finished.
 * Shows the participants with the current result between them.
 *
 * @param root0 component props
 * @param root0.participants participants in the event
 * @param root0.eventDescription event description with event participants
 * @param root0.eventStatus event status from sport data
 * @param root0.sportType sport type for the draw
 * @param root0.matchStart match start date time
 * @param root0.statusTime latest status time from sport data
 * @param root0.matchResultState client match result state
 * @param root0.result result of the event as a tuple containing home and away scores
 * @param root0.isCancelled whether the event is cancelled
 * @returns The SportinfoModalHeaderResultPresentational component.
 */
export function SportinfoModalHeaderResultPresentational(t0) {
  const $ = _c(37);
  const {
    participants,
    eventDescription,
    eventStatus,
    sportType,
    matchStart,
    statusTime,
    matchResultState,
    result,
    isCancelled
  } = t0;
  const resultText = isCancelled ? "Inst\xE4lld" : result ? `${result[0]} - ${result[1]}` : "-";
  let t1;
  if ($[0] !== eventStatus || $[1] !== matchResultState || $[2] !== matchStart || $[3] !== sportType || $[4] !== statusTime) {
    t1 = {
      eventStatus,
      sportType,
      matchStart,
      statusTime,
      matchResultState
    };
    $[0] = eventStatus;
    $[1] = matchResultState;
    $[2] = matchStart;
    $[3] = sportType;
    $[4] = statusTime;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const matchStatusText = useGetMatchStatus(t1);
  if (!participants || !participants[0] || !participants[1]) {
    const t2 = `${eventDescription}, ${matchStatusText}, resultat ${resultText}`;
    let t3;
    if ($[6] !== matchResultState || $[7] !== matchStatusText) {
      t3 = <SportinfoModalMatchStatus matchStatusText={matchStatusText} matchResultState={matchResultState} />;
      $[6] = matchResultState;
      $[7] = matchStatusText;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== eventDescription) {
      t4 = <span aria-hidden={true} translate="no">{eventDescription}</span>;
      $[9] = eventDescription;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== isCancelled || $[12] !== result) {
      t5 = <SportinfoModalCouponResult result={result} isCancelled={isCancelled} />;
      $[11] = isCancelled;
      $[12] = result;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    let t6;
    if ($[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
      t6 = <div aria-label={t2} className="text-layer-fg grid grid-flow-row bg-white p-2 text-center">{t3}{t4}{t5}</div>;
      $[14] = t2;
      $[15] = t3;
      $[16] = t4;
      $[17] = t5;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    return t6;
  }
  const t2 = `${eventDescription}, ${matchStatusText}, resultat ${resultText}`;
  let t3;
  if ($[19] !== matchResultState || $[20] !== matchStatusText) {
    t3 = <SportinfoModalMatchStatus matchStatusText={matchStatusText} matchResultState={matchResultState} />;
    $[19] = matchResultState;
    $[20] = matchStatusText;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== participants[0]) {
    t4 = <ParticipantName className="text-md text-right font-medium" participant={participants[0]} nameSize="full" />;
    $[22] = participants[0];
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  let t5;
  if ($[24] !== isCancelled || $[25] !== result) {
    t5 = <SportinfoModalCouponResult result={result} isCancelled={isCancelled} />;
    $[24] = isCancelled;
    $[25] = result;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== participants[1]) {
    t6 = <ParticipantName className="text-md text-left font-medium" participant={participants[1]} nameSize="full" />;
    $[27] = participants[1];
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== t4 || $[30] !== t5 || $[31] !== t6) {
    t7 = <div aria-hidden={true} className="grid grid-cols-[1fr_auto_1fr] items-center justify-center gap-2">{t4}{t5}{t6}</div>;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== t2 || $[34] !== t3 || $[35] !== t7) {
    t8 = <div aria-label={t2} className="text-layer-fg bg-white p-2 text-center">{t3}{t7}</div>;
    $[33] = t2;
    $[34] = t3;
    $[35] = t7;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  return t8;
}